import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

interface GuideTooltipProps {
  targetRef: React.RefObject<HTMLElement | null>;
  message: string;
  show: boolean;
}

const GuideTooltip: React.FC<GuideTooltipProps> = ({ targetRef, message, show }) => {
  const [position, setPosition] = useState({ top: 0, left: 0, placement: 'top' });
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!show) return;

    const updatePosition = () => {
      if (!targetRef.current || !tooltipRef.current) return;

      const targetRect = targetRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const padding = 16;

      let top = 0;
      let left = 0;
      let placement = 'top';

      // Try to place on top
      if (targetRect.top - tooltipRect.height - padding > 0) {
        placement = 'top';
        top = targetRect.top - tooltipRect.height - padding;
        left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);
      }
      // Try to place on bottom
      else if (targetRect.bottom + tooltipRect.height + padding < viewportHeight) {
        placement = 'bottom';
        top = targetRect.bottom + padding;
        left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);
      }
      // Try to place on right
      else if (targetRect.right + tooltipRect.width + padding < viewportWidth) {
        placement = 'right';
        top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2);
        left = targetRect.right + padding;
      }
      // Try to place on left
      else if (targetRect.left - tooltipRect.width - padding > 0) {
        placement = 'left';
        top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2);
        left = targetRect.left - tooltipRect.width - padding;
      }
      // Fallback to top, adjust horizontally if needed
      else {
        placement = 'top';
        top = targetRect.top - tooltipRect.height - padding;
        left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);
      }

      // Ensure tooltip stays within viewport horizontally
      if (left < padding) {
        left = padding;
      } else if (left + tooltipRect.width > viewportWidth - padding) {
        left = viewportWidth - tooltipRect.width - padding;
      }

      // Ensure tooltip stays within viewport vertically
      if (top < padding) {
        top = padding;
      } else if (top + tooltipRect.height > viewportHeight - padding) {
        top = viewportHeight - tooltipRect.height - padding;
      }

      setPosition({ top, left, placement });
    };

    // Initial position update with a slight delay to ensure DOM is ready
    const timeoutId = setTimeout(updatePosition, 10);
    updatePosition();

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [show, targetRef]);

  if (!show) return null;

  const getArrowStyle = () => {
    const arrowSize = 10;
    const { placement } = position;

    const baseArrowStyle: React.CSSProperties = {
      position: 'absolute',
      width: 0,
      height: 0,
    };

    switch (placement) {
      case 'top':
        return {
          ...baseArrowStyle,
          bottom: -arrowSize,
          left: '50%',
          transform: 'translateX(-50%)',
          borderLeft: `${arrowSize}px solid transparent`,
          borderRight: `${arrowSize}px solid transparent`,
          borderTop: `${arrowSize}px solid #2d3748`,
        };
      case 'bottom':
        return {
          ...baseArrowStyle,
          top: -arrowSize,
          left: '50%',
          transform: 'translateX(-50%)',
          borderLeft: `${arrowSize}px solid transparent`,
          borderRight: `${arrowSize}px solid transparent`,
          borderBottom: `${arrowSize}px solid #2d3748`,
        };
      case 'left':
        return {
          ...baseArrowStyle,
          right: -arrowSize,
          top: '50%',
          transform: 'translateY(-50%)',
          borderTop: `${arrowSize}px solid transparent`,
          borderBottom: `${arrowSize}px solid transparent`,
          borderLeft: `${arrowSize}px solid #2d3748`,
        };
      case 'right':
        return {
          ...baseArrowStyle,
          left: -arrowSize,
          top: '50%',
          transform: 'translateY(-50%)',
          borderTop: `${arrowSize}px solid transparent`,
          borderBottom: `${arrowSize}px solid transparent`,
          borderRight: `${arrowSize}px solid #2d3748`,
        };
      default:
        return baseArrowStyle;
    }
  };

  const tooltipContent = (
    <div
      ref={tooltipRef}
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 10001,
        pointerEvents: 'none',
        visibility: targetRef.current ? 'visible' : 'hidden',
      }}
    >
      <div
        style={{
          background: 'linear-gradient(135deg, #2d3748 0%, #1a202c 100%)',
          color: 'white',
          padding: '12px 18px',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          maxWidth: '300px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)',
          position: 'relative',
          animation: 'tooltipBounce 0.5s ease-out',
          border: '2px solid rgba(99, 179, 237, 0.3)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
          <span style={{ fontSize: '20px', flexShrink: 0 }}>💡</span>
          <div>
            {message}
          </div>
        </div>
        <div style={getArrowStyle()} />
      </div>
    </div>
  );

  return createPortal(tooltipContent, document.body);
};

export default GuideTooltip;
